import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProductCard } from '../components/common/ProductCard';
import { getProducts } from '../utils/api';
import { Filter, X } from 'lucide-react';
import { useLanguage } from '../store/languageContext';
import translate from '../utils/translation';

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [rawProducts, setRawProducts] = useState([]);
    const [parsedRawProducts, setParsedRawProducts] = useState([]);
    const [filters, setFilters] = useState({ brand: '', category: '', minPrice: 0, maxPrice: 10000, search: '', sort: 'update_date' });
    const [selectedSpecs, setSelectedSpecs] = useState({});
    const [metadata, setMetadata] = useState({ minPrice: 0, maxPrice: 10000, uniqueSpecs: {} });
    const [availableSpecs, setAvailableSpecs] = useState({});
    const [specCounts, setSpecCounts] = useState({});
    const [filtersOpen, setFiltersOpen] = useState(false);

    const sectionRef = useRef(null);
    const { language, setNavColor } = useLanguage();
    const t = (key) => translate[language]?.[key] || key;

    useEffect(() => {
        getProducts({ limit: 1000 }).then(res => {
            const data = res.data || [];
            if (data.length === 0) return;

            const prices = data.map(p => parseFloat(p.price || 0));
            const minP = prices.length > 0 ? Math.min(...prices) : 0;
            const maxP = prices.length > 0 ? Math.max(...prices) : 10000;

            const specsMap = data.reduce((acc, p) => {
                let specs = {};
                try {
                    specs = typeof p.specifications === 'string' ? JSON.parse(p.specifications) : (p.specifications || {});
                } catch (e) {
                    specs = {};
                }
                Object.entries(specs).forEach(([k, v]) => {
                    if (!acc[k]) acc[k] = new Set();
                    acc[k].add(v);
                });
                return acc;
            }, {});

            setMetadata({ minPrice: minP, maxPrice: maxP, uniqueSpecs: specsMap });
            setFilters(prev => ({ ...prev, maxPrice: maxP }));
        }).catch(err => console.error('Error fetching metadata:', err));
    }, []);

    useEffect(() => {
        getProducts(filters).then(res => {
            const raw = res.data || [];
            setRawProducts(raw);
        }).catch(err => {
            console.error('Error fetching products:', err);
            setRawProducts([]);
        });
    }, [filters]);

    useEffect(() => {
        const parsed = rawProducts.map(p => ({
            ...p,
            specs: (() => {
                try {
                    return typeof p.specifications === 'string' ? JSON.parse(p.specifications) : (p.specifications || {});
                } catch (e) {
                    return {};
                }
            })()
        }));
        setParsedRawProducts(parsed);
    }, [rawProducts]);

    useEffect(() => {
        if (parsedRawProducts.length === 0) {
            setAvailableSpecs({});
            return;
        }
        const specsMap = parsedRawProducts.reduce((acc, p) => {
            Object.entries(p.specs).forEach(([k, v]) => {
                if (!acc[k]) acc[k] = new Set();
                acc[k].add(v);
            });
            return acc;
        }, {});
        setAvailableSpecs(specsMap);
    }, [parsedRawProducts]);

    useEffect(() => {
        if (parsedRawProducts.length === 0 || Object.keys(availableSpecs).length === 0) {
            setSpecCounts({});
            return;
        }
        const counts = {};
        Object.entries(availableSpecs).forEach(([key, valSet]) => {
            counts[key] = {};
            Array.from(valSet).forEach(value => {
                const count = parsedRawProducts.filter(p => {
                    let matchesOthers = Object.entries(selectedSpecs).every(([oKey, oVals]) =>
                        oKey === key || oVals.has(p.specs[oKey])
                    );
                    return matchesOthers && p.specs[key] === value;
                }).length;
                counts[key][value] = count;
            });
        });
        setSpecCounts(counts);
    }, [parsedRawProducts, selectedSpecs, availableSpecs]);

    useEffect(() => {
        const filteredProducts = parsedRawProducts.filter(p =>
            Object.entries(selectedSpecs).every(([key, vals]) => vals.has(p.specs[key]))
        );
        setProducts(filteredProducts);
    }, [parsedRawProducts, selectedSpecs]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setNavColor('white');
                }
            },
            {
                threshold: 0.1,
                rootMargin: '-50px 0px -20% 0px',
            }
        );

        const currentRef = sectionRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [setNavColor]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSpecChange = (key, value, isChecked) => {
        setSelectedSpecs(prev => {
            const newState = { ...prev };
            if (!newState[key]) {
                newState[key] = new Set();
            }
            if (isChecked) {
                newState[key].add(value);
            } else {
                newState[key].delete(value);
                if (newState[key].size === 0) {
                    delete newState[key];
                }
            }
            return newState;
        });
    };

    const FiltersContent = () => (
        <>
            <div className="flex items-center justify-between mb-4 lg:mb-0">
                <h3 className="font-bold text-lg">{t('filters')}</h3>
                <button
                    onClick={() => setFiltersOpen(false)}
                    className="lg:hidden p-1 rounded hover:bg-white/10"
                >
                    <X className="size-5" />
                </button>
            </div>
            <div className="mb-4">
                <label className="block mb-1 text-sm">{t('minPrice')}</label>
                <input
                    type="number"
                    min={metadata.minPrice}
                    max={metadata.maxPrice}
                    value={filters.minPrice}
                    onChange={e => handleFilterChange('minPrice', parseFloat(e.target.value) || 0)}
                    className="border p-2 w-full outline-none bg-transparent border-[#555] text-white rounded text-sm"
                />
            </div>
            <div className="mb-4">
                <label className="block mb-1 text-sm">{t('maxPrice')}</label>
                <input
                    type="number"
                    min={metadata.minPrice}
                    max={metadata.maxPrice}
                    value={filters.maxPrice}
                    onChange={e => handleFilterChange('maxPrice', parseFloat(e.target.value) || metadata.maxPrice)}
                    className="border p-2 w-full outline-none bg-transparent border-[#555] text-white rounded text-sm"
                />
            </div>
            {Object.entries(availableSpecs).map(([key, valSet]) => (
                <div key={key} className="mb-4">
                    <h4 className="font-bold mb-2 text-sm sm:text-base capitalize">{key}</h4>
                    {Array.from(valSet).map(val => {
                        const isSelected = (selectedSpecs[key] || new Set()).has(val);
                        const count = specCounts[key]?.[val] || 0;
                        const disabled = count === 0 && !isSelected;
                        const id = `spec-${key}-${val}`;
                        return (
                            <div key={val} className="flex items-center mb-1.5">
                                <input
                                    type="checkbox"
                                    id={id}
                                    className="hidden"
                                    checked={isSelected}
                                    onChange={e => handleSpecChange(key, val, e.target.checked)}
                                    disabled={disabled}
                                />
                                <label
                                    htmlFor={id}
                                    className={`relative inline-flex items-center cursor-pointer select-none text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-flex w-5 h-5 sm:w-6 sm:h-6 border-2 rounded items-center justify-center ${language === 'ar' ? 'ml-2' : 'mr-2'} transition-colors ${isSelected
                                            ? 'border-blue-500 text-blue-500'
                                            : 'border-gray-500 text-gray-500'
                                            } ${disabled ? 'opacity-50' : ''}`}
                                    >
                                        {isSelected && (
                                            <svg
                                                className="w-3 h-3 sm:w-4 sm:h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        )}
                                    </span>
                                    <span className={disabled ? 'opacity-50' : ''}>
                                        {String(val)} <span className='text-red-400'>({count})</span>
                                    </span>
                                </label>
                            </div>
                        );
                    })}
                </div>
            ))}
        </>
    );

    return (
        <div ref={sectionRef} className="p-3 sm:p-4 md:p-6 lg:p-8 pt-[90px] sm:pt-[100px] bg-[#222] min-h-screen">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 justify-between items-stretch sm:items-center">
                <div className="flex flex-col xs:flex-row gap-2 sm:gap-3 flex-1">
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={filters.search}
                        onChange={e => handleFilterChange('search', e.target.value)}
                        className="border p-2.5 sm:p-2 bg-transparent text-white outline-none border-[#555] rounded w-full sm:w-48 md:w-64 text-sm"
                    />
                    <select
                        onChange={e => handleFilterChange('sort', e.target.value)}
                        value={filters.sort}
                        className="border p-2.5 sm:p-2 bg-transparent text-white outline-none border-[#555] rounded w-full sm:w-48 md:w-64 text-sm"
                    >
                        <option value="update_date">{t('sortLatest')}</option>
                        <option value="price_asc">{t('sortPriceLowToHigh')}</option>
                        <option value="price_desc">{t('sortPriceHighToLow')}</option>
                    </select>
                </div>
                <button
                    onClick={() => setFiltersOpen(true)}
                    className="lg:hidden flex items-center justify-center gap-2 px-4 py-2.5 bg-[#323232] text-white rounded border border-[#555] text-sm"
                >
                    <Filter className="size-4" />
                    {t('filters')}
                </button>
            </div>

            <div className="flex gap-4">
                {filtersOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setFiltersOpen(false)}
                    />
                )}
                <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-72 sm:w-80 lg:w-64 flex-shrink-0 bg-[#323232] text-white p-4 rounded-r-lg lg:rounded overflow-y-auto transform transition-transform duration-300 ${filtersOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
                    <FiltersContent />
                </aside>

                <div className="flex-1 min-w-0">
                    <AnimatePresence>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                            {products?.map(product => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <ProductCard product={product} />
                                </motion.div>
                            ))}
                        </div>
                    </AnimatePresence>
                    {products.length === 0 && (
                        <div className="text-center text-gray-400 py-16 text-sm sm:text-base">
                            No products found
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;